// src/pages/qc/FgNcDetailModal.jsx
import { useState } from "react";
import { ImageUpload } from "../../components/ImageUpload";
import { todayDubai } from "../../utils/dateUtils";

const TODAY = todayDubai();

const DISPOSITION_LABELS = {
  APPROVE:               "Approve",
  APPROVE_CONDITIONALLY: "Approve Conditionally",
  REWORK:                "Rework",
  REJECT_RECYCLE:        "Reject and Recycle",
  DESTROY:               "Destroy",
};

const STATUS_STYLE = {
  OPEN:                  { bg: "#fef3c7", color: "#92400e" },
  PRODUCTION_RESPONDED:  { bg: "#dbeafe", color: "#1e40af" },
  CLOSED:                { bg: "#d1fae5", color: "#065f46" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? { bg: "#f3f4f6", color: "#374151" };
  const label = status === "OPEN" ? "Open"
              : status === "PRODUCTION_RESPONDED" ? "Production Responded"
              : status === "CLOSED" ? "Closed" : status;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
      {label}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{value || "—"}</div>
    </div>
  );
}

function TwoCol({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>{children}</div>;
}

function SectionHeader({ title }) {
  return (
    <div style={{ background: "#1a3a6b", color: "#fff", fontWeight: 600, fontSize: 12,
      padding: "5px 10px", margin: "14px 0 10px", borderRadius: 3 }}>
      {title}
    </div>
  );
}

function YNSelect({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label className="form-label">{label}</label>
      <select className="form-select" value={value === null ? "" : String(value)}
        onChange={e => onChange(e.target.value === "" ? null : e.target.value === "true")}>
        <option value="">— Select —</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </div>
  );
}

// ── Production Response sub-form ──────────────────────────────────────────────
function ProductionTab({ nc, onSave, onSaved, canEdit }) {
  const [form, setForm] = useState({
    productionEngineer: nc.productionEngineer || "",
    operatorName:       nc.operatorName       || "",
    mixerNumber:        nc.mixerNumber        || "",
    rootCause:          nc.rootCause          || "",
    preventiveAction:   nc.preventiveAction   || "",
    implDate:           nc.implDate           || "",
    implPerson:         nc.implPerson         || "",
    prodSignature:      nc.prodSignature      || "",
    prodRespDate:       nc.prodRespDate       || TODAY,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  if (!canEdit && nc.status !== "OPEN") {
    return (
      <div>
        <TwoCol>
          <Field label="Production Engineer"       value={nc.productionEngineer} />
          <Field label="Operator"                  value={nc.operatorName} />
          <Field label="Mixer Number"              value={nc.mixerNumber} />
          <Field label="Response Date"             value={nc.prodRespDate} />
          <Field label="Implementation Date"       value={nc.implDate} />
          <Field label="Person Responsible"        value={nc.implPerson} />
          <Field label="Signature"                 value={nc.prodSignature} />
        </TwoCol>
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4 }}>Root Cause</div>
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
            padding: "8px 12px", borderRadius: 4, fontSize: 13, whiteSpace: "pre-wrap" }}>
            {nc.rootCause || "—"}
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4 }}>Action to Prevent Recurrence</div>
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
            padding: "8px 12px", borderRadius: 4, fontSize: 13, whiteSpace: "pre-wrap" }}>
            {nc.preventiveAction || "—"}
          </div>
        </div>
      </div>
    );
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        implDate:     form.implDate     || null,
        prodRespDate: form.prodRespDate || null,
      };
      await onSave(body);
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSave}>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Production Engineer</label>
          <input className="form-input" value={form.productionEngineer}
            onChange={e => set("productionEngineer", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Operator</label>
          <input className="form-input" value={form.operatorName}
            onChange={e => set("operatorName", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Mixer Number</label>
          <input className="form-input" value={form.mixerNumber}
            onChange={e => set("mixerNumber", e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Root Cause</label>
        <textarea className="form-input" rows={6} value={form.rootCause}
          onChange={e => set("rootCause", e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Action to Prevent Recurrence</label>
        <textarea className="form-input" rows={6} value={form.preventiveAction}
          onChange={e => set("preventiveAction", e.target.value)} />
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Implementation Date</label>
          <input className="form-input" type="date" value={form.implDate}
            onChange={e => set("implDate", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Person Responsible</label>
          <input className="form-input" value={form.implPerson}
            onChange={e => set("implPerson", e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Signature</label>
          <input className="form-input" value={form.prodSignature}
            onChange={e => set("prodSignature", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Response Date</label>
          <input className="form-input" type="date" value={form.prodRespDate}
            onChange={e => set("prodRespDate", e.target.value)} />
        </div>
      </div>
      {error && <div className="alert-warn">{error}</div>}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving…" : "Save Production Response"}
        </button>
      </div>
    </form>
  );
}

// ── Close/Review sub-form ─────────────────────────────────────────────────────
function CloseTab({ nc, onClose, onSaved, canEdit }) {
  const [form, setForm] = useState({
    followupAudit: nc.followupAudit ?? null,
    caAccepted:    nc.caAccepted    ?? null,
    caEffective:   nc.caEffective   ?? null,
    reviewedBy:    nc.reviewedBy    || "",
    reviewedDate:  nc.reviewedDate  || TODAY,
    disposition:   nc.disposition   || "",
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  const yn = v => v === true ? "Yes" : v === false ? "No" : "—";

  if (nc.status === "CLOSED") {
    return (
      <div>
        <TwoCol>
          <Field label="Follow-up Audit Required"   value={yn(nc.followupAudit)} />
          <Field label="Corrective Action Accepted" value={yn(nc.caAccepted)} />
          <Field label="Corrective Action Effective" value={yn(nc.caEffective)} />
          <Field label="Reviewed By"    value={nc.reviewedBy} />
          <Field label="Review Date"    value={nc.reviewedDate} />
          <Field label="Final Disposition" value={DISPOSITION_LABELS[nc.disposition] || nc.disposition} />
        </TwoCol>
      </div>
    );
  }

  if (!canEdit) return <div style={{ color: "#aaa", fontSize: 12 }}>Not closed yet.</div>;

  async function handleClose(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        reviewedDate: form.reviewedDate || null,
        disposition:  form.disposition  || null,
      };
      await onClose(body);
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleClose}>
      <TwoCol>
        <YNSelect label="Follow-up Audit Required"   value={form.followupAudit}
          onChange={v => set("followupAudit", v)} />
        <YNSelect label="Corrective Action Accepted" value={form.caAccepted}
          onChange={v => set("caAccepted", v)} />
        <YNSelect label="Corrective Action Effective" value={form.caEffective}
          onChange={v => set("caEffective", v)} />
      </TwoCol>
      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Reviewed By</label>
          <input className="form-input" value={form.reviewedBy}
            onChange={e => set("reviewedBy", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Review Date</label>
          <input className="form-input" type="date" value={form.reviewedDate}
            onChange={e => set("reviewedDate", e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Final Disposition (overrides if changed)</label>
        <select className="form-select" value={form.disposition}
          onChange={e => set("disposition", e.target.value)}>
          <option value="">— Keep existing —</option>
          {Object.entries(DISPOSITION_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>
      {error && <div className="alert-warn">{error}</div>}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Closing…" : "Close NC Report"}
        </button>
      </div>
    </form>
  );
}

// ── Main detail modal ─────────────────────────────────────────────────────────
export function FgNcDetailModal({ nc, onProductionSave, onClose, onCancel, canEdit }) {
  const [tab, setTab] = useState("part1");

  const tabs = [
    { id: "part1",       label: "Part 1 — Details" },
    { id: "production",  label: "Part 2 — Production" },
    { id: "review",      label: "Part 3 — Review" },
    { id: "attachments", label: "Attachments" },
  ];

  return (
    <div className="modal" style={{ width: 640, maxHeight: "90vh", overflowY: "auto" }}>
      <div className="modal-header">
        <div>
          <div className="modal-title">{nc.ncNumber}</div>
          <StatusBadge status={nc.status} />
        </div>
        <button className="modal-close" onClick={onCancel}>✕</button>
      </div>

      <div style={{ display: "flex", borderBottom: "2px solid #e5e7eb", padding: "0 16px" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "8px 14px", fontSize: 12, fontWeight: 600,
              color: tab === t.id ? "#1a3a6b" : "#6b7280",
              borderBottom: tab === t.id ? "3px solid #1a3a6b" : "3px solid transparent",
              marginBottom: -2,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="modal-body">
        {tab === "part1" && (
          <div>
            <TwoCol>
              <Field label="Product Name"     value={nc.productName} />
              <Field label="Batch Number"     value={nc.batchNumber} />
              <Field label="Production Date"  value={nc.productionDate} />
              <Field label="Test Date"        value={nc.testDate} />
              <Field label="Reported By"      value={nc.reportedBy} />
              <Field label="Due Date"         value={nc.dueDate} />
              <Field label="Immediate Action" value={DISPOSITION_LABELS[nc.disposition] || nc.disposition} />
            </TwoCol>
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4 }}>Findings</div>
              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
                padding: "8px 12px", borderRadius: 4, fontSize: 13, whiteSpace: "pre-wrap" }}>
                {nc.findings}
              </div>
            </div>
            {nc.immediateCauses && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4 }}>Immediate Causes</div>
                <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb",
                  padding: "8px 12px", borderRadius: 4, fontSize: 13, whiteSpace: "pre-wrap" }}>
                  {nc.immediateCauses}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "production" && (
          <div>
            <SectionHeader title="Part 2 — Production Response" />
            <ProductionTab nc={nc} onSave={onProductionSave} onSaved={() => setTab("part1")} canEdit={canEdit} />
          </div>
        )}

        {tab === "review" && (
          <div>
            <SectionHeader title="Part 3 — MR / GM Review" />
            <CloseTab nc={nc} onClose={onClose} onSaved={() => setTab("part1")} canEdit={canEdit} />
          </div>
        )}

        {tab === "attachments" && (
          <ImageUpload entityType="FGNC" entityId={nc.id}
            canEdit={canEdit && nc.status !== "CLOSED"} />
        )}
      </div>
    </div>
  );
}
